import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { Info } from "lucide-react";
import type { FacilityNode } from "@arc/contracts";
import type { FacilityTreeNode } from "../facility-tree.js";
import type { AuthSession } from "../../../auth/keycloak.js";
import { FacilityCountsMeta } from "../FacilityCountsMeta.js";
import { FacilityManualsView } from "../FacilityManualsView.js";
import { FacilityDetailsDrawer } from "../FacilityDetailsDrawer.js";
import { ManualsToolbarControls } from "../ManualsToolbarControls.js";
import { useFacilityManuals } from "../useFacilityManuals.js";
import { EMPTY_MANUAL_FILTERS, facilityCreators, type ManualFilters } from "../manual-filters.js";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { PageBackRow } from "@/components/PageBackRow";
import { useTruncated } from "@/lib/useTruncated";
import { FacilityBreadcrumb } from "./FacilityBreadcrumb.js";

/**
 * The manuals view for one facility, opened from a card/header arrow (FN / ADR-014). Facilities and manuals never share
 * a view now, so this is a full-width screen: a breadcrumb + the facility name/counts + a Back control, then the reused
 * {@link FacilityManualsView} grid. `canManageManuals` is computed by the caller (the server is authoritative).
 */
export function ManualsViewShell({
  facility,
  ancestors,
  session,
  canManageManuals,
  canConfirmManuals,
  canManageFacility,
  onHome,
  onCrumb,
  onBack,
  kiosk = false,
}: {
  facility: FacilityTreeNode;
  ancestors: readonly (FacilityNode | null)[];
  session: AuthSession | null;
  canManageManuals: boolean;
  /** The caller has the facility `confirm` tier — reveals the publish/unpublish/clear-flag review transitions (ADR-018).
   *  A subset of `canManageManuals` (confirm ⊃ manuals_manage in the cascade). */
  canConfirmManuals: boolean;
  /** Facility-management capability (manage/access_manage/bypass) — gates the Area-info drawer's Manage mode. Distinct
   *  from `canManageManuals` (the wider manuals gate). */
  canManageFacility: boolean;
  onHome: () => void;
  onCrumb: (id: number) => void;
  onBack: () => void;
  /** Shop-floor kiosk (ADR-036): drops the authoring and personal affordances from the manuals screen entirely. */
  kiosk?: boolean;
}) {
  const { t } = useTranslation();
  // The Area-info drawer (opened from the header ⓘ).
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Search lives UP here on the title row (mockup #15) — the grid reads it via props.
  const [search, setSearch] = useState("");
  // "More filters" (mockup #16) — owned here (the sliders button is in the header). The Creator combobox offers only
  // THIS facility's creators, derived from the manuals list (cache-shared with the grid's queries — no extra fetch).
  const [advFilters, setAdvFilters] = useState<ManualFilters>(EMPTY_MANUAL_FILTERS);
  const activeManuals = useFacilityManuals(session, facility.id, false);
  const archivedManuals = useFacilityManuals(session, facility.id, true);
  const creators = useMemo(
    () => facilityCreators(activeManuals.data ?? [], archivedManuals.data ?? []),
    [activeManuals.data, archivedManuals.data],
  );
  // Reset the advanced filters (and close the popovers) when the facility changes — the search reset is handled by the
  // grid's own [facilityId] effect (it calls our setSearch).
  useEffect(() => {
    setAdvFilters(EMPTY_MANUAL_FILTERS);
    setDetailsOpen(false);
  }, [facility.id]);
  return (
    <section aria-label={t("facilitySelection.nav.manualsRegion")} className="flex min-h-0 flex-1 flex-col">
      {/* Header (no bottom border — the filter row below carries the divider, so header + filters read as one block).
          Constrained to the SAME centred column as the content, so the whole view shares one set of side margins;
          within it the breadcrumb/title sit left and the controls right (the content below then centres inside it). */}
      <div className="bg-background pt-4">
        <div className="page-column flex flex-col gap-3">
          <PageBackRow label={t("facilitySelection.nav.back")} onBack={onBack}>
            <FacilityBreadcrumb ancestors={ancestors} current={facility} onHome={onHome} onCrumb={onCrumb} />
            <FacilityCountsMeta
              className="ml-auto text-sm"
              subfacilities={facility.childCount}
              categories={facility.categoryCount ?? 0}
              manuals={facility.manualCount ?? 0}
            />
          </PageBackRow>
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
            <div className="min-w-0">
              {/* Area-info sits AFTER the name. The name is the sole <h1> here (the generic "Facility selection" heading
                  is hidden in manuals mode — see FacilityDrilldown). */}
              <div className="flex items-center gap-2">
                <FacilityTitle name={facility.name} />
                {/* The Area-info drawer lists the archived manuals, which would put withdrawn instructions one tap away
                    on a shop-floor screen — so a kiosk does not get the drawer at all (ADR-036). */}
                {kiosk ? null : (
                  <TooltipTrigger delay={300}>
                    <Button variant="outline" size="icon-sm" aria-label={t("facilitySelection.nav.areaInfo")} onPress={() => setDetailsOpen(true)} className="shrink-0">
                      <Info aria-hidden="true" className="size-4" />
                    </Button>
                    <Tooltip>{t("facilitySelection.nav.areaInfo")}</Tooltip>
                  </TooltipTrigger>
                )}
              </div>
            </div>
            {/* Search · filters — the title-row controls (#15). */}
            <ManualsToolbarControls
              search={search}
              onSearchChange={setSearch}
              filters={advFilters}
              onFiltersChange={setAdvFilters}
              creators={creators}
            />
          </div>
        </div>
      </div>
      <FacilityManualsView
        session={session}
        facilityId={facility.id}
        canManage={canManageManuals}
        canConfirm={canConfirmManuals}
        searchValue={search}
        onSearchChange={setSearch}
        advancedFilters={advFilters}
        kiosk={kiosk}
      />
      {detailsOpen ? (
        <FacilityDetailsDrawer
          facilityId={facility.id}
          facilityName={facility.name}
          childCount={facility.childCount}
          categoryCount={facility.categoryCount ?? 0}
          permission={facility.permission}
          canManage={canManageFacility}
          session={session}
          initialMode="overview"
          canManageManuals={canManageManuals}
          onClose={() => setDetailsOpen(false)}
        />
      ) : null}
    </section>
  );
}

/** The facility name as the page `<h1>` — truncates, and (only when truncated) reveals the full name in a tooltip on
 *  hover/focus (never the native `title`). */
function FacilityTitle({ name }: { name: string }) {
  const { ref, isTruncated, measure } = useTruncated<HTMLHeadingElement>(name);
  return (
    <TooltipTrigger delay={0} isDisabled={!isTruncated} excludeFromTabOrder>
      <h1 ref={ref} onPointerEnter={measure} className="min-w-0 truncate text-2xl font-bold tracking-tight">
        {name}
      </h1>
      <Tooltip>{name}</Tooltip>
    </TooltipTrigger>
  );
}
